import {
  Component,
  ElementRef,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import _ = require('lodash');
import moment = require('moment');
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { isValidDate } from '../../../Shared/date-helper';
import { DepositSearchResult } from '../../../models/depositSearchResult.model';
import { FormNode } from '../../../models/form-node';
import { RemittanceEntry } from '../../../models/remittance-entry.model';
import { StoreIdentifier } from '../../../models/store-identifier.model';
import { AuthorizationService } from '../../../services/authorization.service';
import { CreateRemittanceService } from '../../../services/remittance-entry/create-remittance.service';
import { RemittanceEntryService } from '../../../services/remittance-entry/remittance-entry.service';
import { StoreIdentifierService } from '../../../services/remittance-entry/store-identifier.service';
import { AdjustmentCodeComponent } from '../adjustment-code/adjustment-code.component';
import { StoreIdentifierComponent } from '../store-identifier/store-identifier.component';

export enum KEYCODES {
  KEYENTER = 13,
  NUMPAD_PLUS = 107,
}

@Component({
  selector: 'app-create-remittance',
  templateUrl: './create-remittance.component.html',
  styleUrls: ['./create-remittance.component.css'],
})

//  This is a Smart component.
export class CreateRemittanceComponent
  implements OnInit, OnDestroy {
  // private FIELD_SELECTOR = '#createRemittanceForm input,#createRemittance'
  private readonly FIELD_SELECTOR = '#createRemittanceForm input'
  private readonly RXNUMBER_SELECTOR = 'input#rxNumber'

  @ViewChild('rxnumber') rxNumberChild: ElementRef;
  @ViewChild('store') storeComponentChild: StoreIdentifierComponent
  @ViewChild('adjustmentCodes') adjustmentCodesChild: AdjustmentCodeComponent

  //  Observables
  private createRemittanceSource = new Subject<RemittanceEntry>()
  createRemittanceSource$ = this.createRemittanceSource.asObservable()

  //  Subscriptions
  onCheckDetail: Subscription
  onStoreFormComplete: Subscription;
  onEditRemittance: Subscription
  onDeleteRemittance: Subscription

  createRemittanceForm: FormGroup;
  formProcessing = false;
  isUpdate = false

  //  Update fields
  private _rxNumberElement
  private _batchId: string
  private _sequenceNumber: number

  private _formNodes: FormNode[] = []

  private _depositSearchResult: DepositSearchResult
  private _storeIdentifier: StoreIdentifier

  //#region Properties
  get depositSearchResult(): DepositSearchResult {
    return this._depositSearchResult
  }

  get storeIdentifier(): StoreIdentifier {
    return this._storeIdentifier
  }

  get store(): FormControl {
    return this.createRemittanceForm.get('store') as FormControl;
  }

  get rxNumber(): FormControl {
    return this.createRemittanceForm.get('rxNumber') as FormControl;
  }

  get fillDate(): FormControl {
    return this.createRemittanceForm.get('fillDate') as FormControl;
  }

  get paidAmount(): FormControl {
    return this.createRemittanceForm.get('paidAmount') as FormControl;
  }

  get invalidDate(): boolean {
    if (
      !this.createRemittanceForm ||
      this.createRemittanceForm.get('fillDate')
    ) {
      return;
    }
    return (
      this.createRemittanceForm.get('fillDate').touched &&
      this.createRemittanceForm.get('fillDate').hasError('invalidDate')
    );
  }

  get isValid(): boolean {
    return ((this.storeIdentifier && this.storeIdentifier.Location_CorporateLocationNumber !== '') &&
      (this.rxNumber.value && this.rxNumber.valid) &&
      (this.fillDate.value && this.fillDate.valid) &&
      (this.paidAmount.value && this.paidAmount.valid));
  }

  //#endregion

  constructor(
    private authorizationService: AuthorizationService,
    private remittanceEntryService: RemittanceEntryService,
    private createRemittanceService: CreateRemittanceService,
    private storeIdentifierService: StoreIdentifierService,
    private builder: FormBuilder,
  ) {
    this.createSubscriptions()
  }

  private createSubscriptions(): void {
    //  Subscribe to Check Detail changes. Normally, this only occurs when we first enter the page.
    this.onCheckDetail = this.remittanceEntryService.checkDetailSource$.subscribe(
      (searchResult) => {
        this._depositSearchResult = searchResult
      },
      (error) => {
        console.log(
          `Error occurred at ${new Date()} status: ${error.status.toString()}`,
        );
      },
      () => {
      }, //  Complete
    );
    //  Subscribe to the Store Form complete notification. This normally occurs
    //  when the user has entered a valid NCPDP/Store/NPI and the component has been
    //  fully populated.
    this.onStoreFormComplete = this.storeIdentifierService.formCompleteSource$.subscribe(
      (storeIdentifier) => {
        console.log('Updating CreateRemittanceComponent._storeIdentifer');
        console.log(storeIdentifier);
        console.log(new Date().getTime());
        this._storeIdentifier = storeIdentifier
        // if (this.rxNumber) {
        //   this.rxNumberChild.nativeElement.focus();
        // } else {  //  Patch for Edge.
        //   this._rxNumberElement = document.getElementById(this.RXNUMBER_SELECTOR)
        //   if (this._rxNumberElement) {
        //     this._rxNumberElement.focus()
        //   }
        // }
      },
      (error) => {
        console.log(
          `Error occurred at ${new Date()} status: ${error.status.toString()}`,
        );
      },
      () => { }, //  Complete
    );

    //  When an edit notification is received, the form needs to be repopulated for editing.
    this.onEditRemittance = this.remittanceEntryService.remittanceStageEditSource$.subscribe((remittance: RemittanceEntry) => {
      this.isUpdate = true
      this.updateForm(remittance)
      return false
    })

    this.onDeleteRemittance = this.remittanceEntryService.remittanceStageDeleteSource$.subscribe((remittance: RemittanceEntry) => {
      if (this.isUpdate) {
        this.isUpdate = false;
        this.resetForm();
      }
    })
  }

  ngOnInit() {
    this.createForm();
    this.buildFormNavigation(this.FIELD_SELECTOR)
  }

  ngOnDestroy(): void {
    this.onStoreFormComplete.unsubscribe();
    this.onCheckDetail.unsubscribe();
    this.onEditRemittance.unsubscribe();
    this.onDeleteRemittance.unsubscribe();
  }

  private async createRemittance() {
    if (this.createRemittanceForm.invalid) {
      return
    }

    this.formProcessing = true;

    //  Default to creating a new remittance.
    let remittance = this.buildRemittanceFromForm()

    // If this is an update operation, replace the batch and sequence number
    // defaults with the values from the iSeries.
    if (this.isUpdate) {
      remittance.Payment_BatchId = this._batchId
      remittance.Batch_SequenceNumber = this._sequenceNumber
    }

    //  Call service to post remittance.
    await this.createRemittanceService.createRemittance(remittance)
      .then(() => {
        toastr.success('Remittance created')
        //  Clear form fields.
        this.resetForm();
        this.resetFlags()
      })
      .catch(() => {
        toastr.error('An error occurred while creating a Remittance Entry.  If this problem continues please contact your account team.');
        this.resetFlags();
      })
      .then(() => {
        this.callNotifyService();
      })
  }

  //  TODO: This seems laborious, investigate whether we can just bind the model to the form directly. -JEB
  private updateForm(remittance: RemittanceEntry): void {
    const fillDate = moment(remittance.Prescription_FillDate)

    this._batchId = remittance.Payment_BatchId
    this._sequenceNumber = remittance.Batch_SequenceNumber

    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0

    this.storeComponentChild.ncpdp.setValue(remittance.Location_CorporateLocationNumber)
    this.storeComponentChild.ncpdp.markAsDirty()
    this.storeComponentChild.storeId.setValue(remittance.StoreId)
    this.storeComponentChild.storeId.markAsDirty()
    this.storeComponentChild.npi.setValue('')
    this.storeComponentChild.npi.markAsPristine()
    this.rxNumber.setValue(remittance.Prescription_PrescriptionNumber)
    this.rxNumber.markAsDirty()
    this.fillDate.setValue(fillDate.format('MMDDYYYY'))
    this.fillDate.markAsDirty()
    this.paidAmount.setValue(Number(remittance.Money_PaidAmount).toFixed(2))
    this.paidAmount.markAsDirty()

    //  Update all the adjustment code fields.
    this.adjustmentCodesChild.adjustmentCode1.setValue(remittance.Adjustment_Code_1)
    this.adjustmentCodesChild.adjustmentCode2.setValue(remittance.Adjustment_Code_2)
    this.adjustmentCodesChild.adjustmentCode3.setValue(remittance.Adjustment_Code_3)
    this.adjustmentCodesChild.adjustmentCode4.setValue(remittance.Adjustment_Code_4)
    this.adjustmentCodesChild.adjustmentCode5.setValue(remittance.Adjustment_Code_5)
    this.adjustmentCodesChild.adjustmentCode6.setValue(remittance.Adjustment_Code_6)
    this.adjustmentCodesChild.adjustmentCode7.setValue(remittance.Adjustment_Code_7)
    this.adjustmentCodesChild.adjustmentCode8.setValue(remittance.Adjustment_Code_8)
    this.adjustmentCodesChild.adjustmentCode9.setValue(remittance.Adjustment_Code_9)
    this.adjustmentCodesChild.adjustmentCode10.setValue(remittance.Adjustment_Code_10)

    //  If the fields have a value, mark them as dirty. Otherwise, mark them as pristine
    //  so we can highlight them accurately.
    //  TODO: This seems ripe for refactoring!
    if (remittance.Adjustment_Code_1) {
      this.adjustmentCodesChild.adjustmentCode1.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode1.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode1.markAsPristine()
    }
    if (remittance.Adjustment_Code_2) {
      this.adjustmentCodesChild.adjustmentCode2.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode2.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode2.markAsPristine()
    }
    if (remittance.Adjustment_Code_3) {
      this.adjustmentCodesChild.adjustmentCode3.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode3.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode3.markAsPristine()
    }
    if (remittance.Adjustment_Code_4) {
      this.adjustmentCodesChild.adjustmentCode4.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode4.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode4.markAsPristine()
    }
    if (remittance.Adjustment_Code_5) {
      this.adjustmentCodesChild.adjustmentCode5.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode5.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode5.markAsPristine()
    }
    if (remittance.Adjustment_Code_6) {
      this.adjustmentCodesChild.adjustmentCode6.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode6.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode6.markAsPristine()
    }
    if (remittance.Adjustment_Code_7) {
      this.adjustmentCodesChild.adjustmentCode7.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode7.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode7.markAsPristine()
    }
    if (remittance.Adjustment_Code_8) {
      this.adjustmentCodesChild.adjustmentCode8.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode8.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode8.markAsPristine()
    }
    if (remittance.Adjustment_Code_9) {
      this.adjustmentCodesChild.adjustmentCode9.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode9.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode9.markAsPristine()
    }
    if (remittance.Adjustment_Code_10) {
      this.adjustmentCodesChild.adjustmentCode10.markAsDirty()
      this.adjustmentCodesChild.adjustmentCode10.enable()
    } else {
      this.adjustmentCodesChild.adjustmentCode10.markAsPristine()
    }

    this.storeComponentChild.ncpdpChild.nativeElement.focus()
  }

  private async updateRemittance() {
    if (this.createRemittanceForm.invalid) {
      return
    }

    this.formProcessing = true;
    this.callNotifyService();

    //  Build the base remittance from the form.
    let remittance = this.buildRemittanceFromForm()

    // Since this is an update operation, replace the batch and sequence number
    // defaults with the values from the iSeries.
    remittance.Payment_BatchId = this._batchId
    remittance.Batch_SequenceNumber = this._sequenceNumber

    //  Call service to post remittance.
    await this.createRemittanceService.updateRemittance(this._batchId, this._sequenceNumber, remittance)
      .then(() => {
        toastr.success('Remittance updated')
        //  Clear form fields.
        this.resetForm();
        this.resetFlags()
      })
      .catch(() => {
        toastr.error('An error occurred while updating a Remittance Entry.  If this problem continues please contact your account team.');
        this.resetFlags();
      })
  }

  // This method builds a Remittance Entry instance using the form values provided by the user.
  private buildRemittanceFromForm(): RemittanceEntry {
    const fillDate = moment(this.fillDate.value, 'MMDDYYYY')

    // This is being added to debug a production issue re: SDHEAL-4255
    console.log('Building the Remittance Entry Model');
    console.log(this.storeIdentifier);
    console.log(new Date().getTime());

    const remittance = new RemittanceEntry(
      '',                                                     //  Batch ID (blank for new remittance)
      0,                                                      //  Sequence Number (zero for new remittance)
      this.depositSearchResult.Check_Ref_Number,              //  Check Ref Number
      this.depositSearchResult.Payer_ShardPayerIdentifier,    //  Payer identifier
      this.storeIdentifier.Location_CorporateLocationNumber,  //  NCPDP
      this.storeIdentifier.StoreId,                           //  Store ID
      this.rxNumber.value,
      fillDate.toDate(),
      Number(this.paidAmount.value),
      this.adjustmentCodesChild.adjustmentCode1.value,
      this.adjustmentCodesChild.adjustmentCode2.value,
      this.adjustmentCodesChild.adjustmentCode3.value,
      this.adjustmentCodesChild.adjustmentCode4.value,
      this.adjustmentCodesChild.adjustmentCode5.value,
      this.adjustmentCodesChild.adjustmentCode6.value,
      this.adjustmentCodesChild.adjustmentCode7.value,
      this.adjustmentCodesChild.adjustmentCode8.value,
      this.adjustmentCodesChild.adjustmentCode9.value,
      this.adjustmentCodesChild.adjustmentCode10.value,
    );
    return remittance
  }

  private resetFlags(): void {
    this.isUpdate = false
    this._batchId = ''
    this._sequenceNumber = 0
    this.formProcessing = false;
  }

  checkForNavigationKey(event: KeyboardEvent) {
    switch (event.keyCode) {
      case KEYCODES.KEYENTER:
        event.preventDefault();
        event.stopPropagation();
        if (!this.isUpdate && !this.formProcessing) {
          this.createRemittance()
        } else if (this.isUpdate && !this.formProcessing) {
          this.updateRemittance()
        }
        break;

      case KEYCODES.NUMPAD_PLUS:
        //  Don't show the key onscreen.
        event.preventDefault();
        event.stopPropagation();

        //  Shift key pressed navigates to previous field.
        let goBackwards = event.shiftKey

        //  Get id of the current control
        let control = (event.target as HTMLElement)

        //  Get the next node in our list.
        let index = _.findIndex(this._formNodes, (fn) => (fn.id === control.id))
        let node = this._formNodes[index]

        //  Default to going forward.
        let newControl = document.getElementById(`${node.next}`)

        //  If the user pressed shift, set the next control to the previous one.
        if (goBackwards) {
          newControl = document.getElementById(`${node.prev}`)
        }

        //  Set focus to the selected control.
        newControl.focus()
        break;

      default:
        break;
    }
  }

  private resetForm(): void {
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0

    //  Once this form is reset, set the focus back to the store NCPDP field by default.
    this.storeComponentChild.ncpdpChild.nativeElement.focus()

    //  Reset all form fields except Store and FillDate.
    this.rxNumber.markAsUntouched()
    this.rxNumber.markAsPristine()
    this.paidAmount.markAsUntouched()
    this.paidAmount.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode1.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode2.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode3.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode4.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode5.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode6.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode7.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode8.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode9.markAsPristine()
    this.adjustmentCodesChild.adjustmentCode10.markAsPristine()

    this.createRemittanceForm.patchValue({
      rxNumber: '',
      paidAmount: '',
    });
    this.adjustmentCodesChild.resetForm()
  }

  isFieldInvalid(field: string): boolean {
    return (
      !this.createRemittanceForm.get(field).valid &&
      this.createRemittanceForm.get(field).dirty
    );
  }

  private createForm() {
    this.createRemittanceForm = this.builder.group({
      store: this.builder.control(''),
      rxNumber: this.builder.control('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(16),
      ]),
      fillDate: this.builder.control('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(8),
        Validators.pattern('^[0-9]{1,2}[0-9]{1,2}[0-9]{4}$'),
        (control: AbstractControl) => isValidDate(control),
      ]),
      paidAmount: this.builder.control('', [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern('^[+-]?[0-9]{1,7}.?[0-9]{0,2}$'),
      ]),
      adjustmentCodes: this.builder.control(''),
    });
    this.storeComponentChild.ncpdpChild.nativeElement.focus()
  }

  private buildFormNavigation(fieldSelector: string): void {
    let fields = Array.from(document.querySelectorAll(fieldSelector))

    if (!fields) {
      return
    }

    //  Create array.
    fields.forEach((field) => {
      let fieldnav = new FormNode(field.id, '', '')
      this._formNodes.push(fieldnav)
    })

    //  Update array pointers.
    _.forEach(this._formNodes, (value: FormNode, index: number) => {
      //  Only one element
      if (index === 0 && index === this._formNodes.length - 1) {
        value.next = value.id
        value.prev = value.id
      } else {
        //  If we're on the first or last element, update to point to
        //  the tail/head respectively.
        if (index === 0 || index === this._formNodes.length - 1) {
          if (index === 0) {
            value.prev = _.last(this._formNodes).id
            value.next = this._formNodes[index + 1].id
          } else if (index === this._formNodes.length - 1) {
            value.prev = this._formNodes[index - 1].id
            value.next = _.first(this._formNodes).id
          }
        } else {  //  Nominal case.
          value.next = this._formNodes[index + 1].id
          value.prev = this._formNodes[index - 1].id
        }
      }
    })
  }

  private callNotifyService() {
    if (!this.authorizationService.HealthcareTopBar || !this.authorizationService.HealthcareTopBar.notifyActivity) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/remittance-entry/create-remittance/create-remittance.component.ts